import type { AppleLoginPayload } from './authTypes';

export interface AppleWebConfig {
  clientId: string;
  redirectURI: string;
}

export interface AppleWebLocationLike {
  hostname: string;
  origin: string;
  protocol: string;
  pathname?: string;
  search?: string;
  hash?: string;
}

export interface AppleWebConfigResolution {
  config: AppleWebConfig | null;
  error: string | null;
}

export interface AppleAuthAttempt {
  state: string;
  createdAt: number;
  returnTo: string;
}

export interface AppleAuthBridgeResult {
  state?: string;
  code?: string;
  id_token?: string;
  user?: unknown;
  error?: string;
  error_description?: string;
  receivedAt?: number;
}

const DEFAULT_APPLE_WEB_CLIENT_ID = 'ai.insightatlas.web';
const DEFAULT_PRODUCTION_APPLE_HOSTS = new Set(['www.insightatlas.ai']);
const LOCAL_APPLE_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0']);
const APPLE_CALLBACK_PATH = '/auth/apple/callback';
const APPLE_AUTH_STORAGE_PREFIX = 'insightatlas.appleAuth.';
const APPLE_AUTH_ATTEMPT_PREFIX = `${APPLE_AUTH_STORAGE_PREFIX}attempt.`;
const APPLE_AUTH_RESULT_PREFIX = `${APPLE_AUTH_STORAGE_PREFIX}result.`;
const APPLE_AUTH_MAX_AGE_MS = 10 * 60 * 1000;

export const APPLE_AUTH_MESSAGE_TYPE = 'insightatlas.appleAuth.result';
export const APPLE_AUTH_RESULT_TIMEOUT_MS = 120 * 1000;

function readEnvValue(value?: string): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed || null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function readString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed || null;
}

function parseUrl(value: string): URL | null {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

function getAppleCallbackUrl(origin: string): string {
  return `${origin}${APPLE_CALLBACK_PATH}`;
}

function getStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function safeRemoveItem(storage: Storage | null, key: string): void {
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(key);
  } catch {
    // Ignore storage cleanup failures.
  }
}

function pruneStaleAppleAuthEntries(): void {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  const cutoff = Date.now() - APPLE_AUTH_MAX_AGE_MS;
  const keysToDelete: string[] = [];

  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (!key) {
      continue;
    }

    if (
      !key.startsWith(APPLE_AUTH_ATTEMPT_PREFIX) &&
      !key.startsWith(APPLE_AUTH_RESULT_PREFIX)
    ) {
      continue;
    }

    const rawValue = storage.getItem(key);
    if (!rawValue) {
      keysToDelete.push(key);
      continue;
    }

    try {
      const parsed = JSON.parse(rawValue) as { createdAt?: number; receivedAt?: number };
      const timestamp = parsed.createdAt ?? parsed.receivedAt ?? 0;
      if (!timestamp || timestamp < cutoff) {
        keysToDelete.push(key);
      }
    } catch {
      keysToDelete.push(key);
    }
  }

  keysToDelete.forEach((key) => safeRemoveItem(storage, key));
}

function createRandomState(): string {
  if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return `apple-${Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, '0')
    ).join('')}`;
  }

  return `apple-${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

function getReturnTo(locationLike: AppleWebLocationLike): string {
  const pathname = typeof locationLike.pathname === 'string' ? locationLike.pathname : '/';
  const search = typeof locationLike.search === 'string' ? locationLike.search : '';
  const hash = typeof locationLike.hash === 'string' ? locationLike.hash : '';
  return `${pathname}${search}${hash}` || '/';
}

function getConfiguredWebConfig(
  locationLike: AppleWebLocationLike,
  env: NodeJS.ProcessEnv
): AppleWebConfigResolution | null {
  const clientId = readEnvValue(env.REACT_APP_APPLE_CLIENT_ID);
  const redirectURI = readEnvValue(env.REACT_APP_APPLE_REDIRECT_URI);

  if (!clientId && !redirectURI) {
    return null;
  }

  if (!clientId || !redirectURI) {
    return {
      config: null,
      error:
        'Apple Sign-In is partially configured. Set both REACT_APP_APPLE_CLIENT_ID and REACT_APP_APPLE_REDIRECT_URI.',
    };
  }

  const parsedRedirect = parseUrl(redirectURI);
  if (!parsedRedirect) {
    return {
      config: null,
      error:
        'Apple Sign-In redirect URI is invalid. REACT_APP_APPLE_REDIRECT_URI must be a valid HTTPS URL.',
    };
  }

  if (parsedRedirect.protocol.toLowerCase() !== 'https:') {
    return {
      config: null,
      error:
        'Apple Sign-In redirect URI must use HTTPS and match a verified Apple web return URL.',
    };
  }

  if (parsedRedirect.origin !== locationLike.origin) {
    return {
      config: null,
      error: `Apple Sign-In must return to the same site origin in popup mode. Current site: ${locationLike.origin}. Configured redirect: ${parsedRedirect.origin}.`,
    };
  }

  return {
    config: {
      clientId,
      redirectURI,
    },
    error: null,
  };
}

export function resolveAppleWebConfig(
  locationLike: AppleWebLocationLike,
  env: NodeJS.ProcessEnv = process.env
): AppleWebConfigResolution {
  const configuredConfig = getConfiguredWebConfig(locationLike, env);
  if (configuredConfig) {
    return configuredConfig;
  }

  const hostname = locationLike.hostname.toLowerCase();
  if (DEFAULT_PRODUCTION_APPLE_HOSTS.has(hostname)) {
    return {
      config: {
        clientId: DEFAULT_APPLE_WEB_CLIENT_ID,
        redirectURI: getAppleCallbackUrl(locationLike.origin),
      },
      error: null,
    };
  }

  if (
    LOCAL_APPLE_HOSTS.has(hostname) ||
    locationLike.protocol.toLowerCase() !== 'https:'
  ) {
    return {
      config: null,
      error:
        'Apple Sign-In on web requires a verified HTTPS domain or tunnel registered in Apple Developer. localhost and insecure origins are not supported by Apple.',
    };
  }

  if (hostname === 'insightatlas.ai') {
    return {
      config: null,
      error:
        'Apple Sign-In is only configured by default for https://www.insightatlas.ai. Configure REACT_APP_APPLE_REDIRECT_URI for this host or use the canonical www domain.',
    };
  }

  return {
    config: null,
    error:
      'Apple Sign-In is not configured. Set REACT_APP_APPLE_CLIENT_ID and REACT_APP_APPLE_REDIRECT_URI to a verified Apple Services ID return URL.',
  };
}

export function getAppleWebConfig(): AppleWebConfigResolution {
  return resolveAppleWebConfig(window.location);
}

export function getAppleAuthAttemptStorageKey(state: string): string {
  return `${APPLE_AUTH_ATTEMPT_PREFIX}${state}`;
}

export function getAppleAuthResultStorageKey(state: string): string {
  return `${APPLE_AUTH_RESULT_PREFIX}${state}`;
}

export function createAppleAuthAttempt(
  locationLike: AppleWebLocationLike = window.location
): AppleAuthAttempt {
  pruneStaleAppleAuthEntries();

  const attempt: AppleAuthAttempt = {
    state: createRandomState(),
    createdAt: Date.now(),
    returnTo: getReturnTo(locationLike),
  };

  const storage = getStorage();
  if (storage) {
    try {
      storage.setItem(getAppleAuthAttemptStorageKey(attempt.state), JSON.stringify(attempt));
    } catch {
      // Ignore storage persistence failures. postMessage is still the primary bridge.
    }
  }

  return attempt;
}

export function getAppleAuthAttempt(state: string): AppleAuthAttempt | null {
  const storage = getStorage();
  if (!storage) {
    return null;
  }

  const rawValue = storage.getItem(getAppleAuthAttemptStorageKey(state));
  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue) as AppleAuthAttempt;
    if (!parsed?.state || parsed.state !== state) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearAppleAuthAttempt(state: string): void {
  safeRemoveItem(getStorage(), getAppleAuthAttemptStorageKey(state));
}

export function clearAppleAuthResult(state: string): void {
  safeRemoveItem(getStorage(), getAppleAuthResultStorageKey(state));
}

export function consumeAppleAuthBridgeResult(state: string): AppleAuthBridgeResult | null {
  const storage = getStorage();
  if (!storage) {
    return null;
  }

  const key = getAppleAuthResultStorageKey(state);
  const rawValue = storage.getItem(key);
  safeRemoveItem(storage, key);

  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue) as AppleAuthBridgeResult;
  } catch {
    return null;
  }
}

export function normalizeAppleCallbackUser(user: unknown): AppleLoginPayload['user'] {
  let parsedUser: unknown = user;
  if (typeof parsedUser === 'string') {
    try {
      parsedUser = JSON.parse(parsedUser);
    } catch {
      parsedUser = null;
    }
  }

  const record = asRecord(parsedUser);
  if (!record) {
    return undefined;
  }

  const nameRecord = asRecord(record.name);
  const email = readString(record.email);
  const explicitName = readString(record.name);
  const firstName =
    readString(nameRecord?.firstName) ??
    readString(record.first_name) ??
    readString(record.firstName);
  const lastName =
    readString(nameRecord?.lastName) ??
    readString(record.last_name) ??
    readString(record.lastName);
  const combinedName =
    [firstName, lastName].filter(Boolean).join(' ').trim() || undefined;
  const fullName = explicitName ?? combinedName;

  if (!email && !firstName && !lastName && !fullName) {
    return undefined;
  }

  return {
    email: email ?? undefined,
    name: fullName,
    first_name: firstName ?? undefined,
    last_name: lastName ?? undefined,
  };
}

export function normalizeAppleAuthResponse(data: unknown): AppleLoginPayload | null {
  const record = asRecord(data);
  if (!record) {
    return null;
  }

  const authorization = asRecord(record.authorization);
  const identityToken =
    readString(authorization?.id_token) ?? readString(record.id_token);

  if (!identityToken) {
    return null;
  }

  return {
    identityToken,
    authorizationCode:
      readString(authorization?.code) ?? readString(record.code) ?? undefined,
    user: normalizeAppleCallbackUser(record.user),
  };
}

function getAppleErrorCode(error: unknown): string | null {
  const record = asRecord(error);
  return readString(record?.error) ?? readString(record?.code);
}

function getAppleErrorDescription(error: unknown): string | null {
  const record = asRecord(error);
  return (
    readString(record?.error_description) ??
    (error instanceof Error ? readString(error.message) : null)
  );
}

export function formatAppleAuthError(error: unknown): string {
  const errorCode = getAppleErrorCode(error);
  const errorDescription = getAppleErrorDescription(error);
  const lowerDescription = errorDescription?.toLowerCase() ?? '';

  if (
    errorCode === 'popup_closed_by_user' ||
    errorCode === 'popup_closed' ||
    errorCode === 'user_cancelled_authorize'
  ) {
    return 'Apple Sign-In was cancelled';
  }

  if (
    errorCode === 'invalid_request' ||
    lowerDescription.includes('same site origin') ||
    lowerDescription.includes('same-site origin') ||
    lowerDescription.includes('redirect uri') ||
    lowerDescription.includes('redirect_uri') ||
    lowerDescription.includes('configured redirect')
  ) {
    return 'Apple Sign-In is configured for a different web origin. Open Insight Atlas on the configured domain and try again.';
  }

  if (lowerDescription.includes('timed out')) {
    return errorDescription || 'Apple Sign-In timed out before Insight Atlas received the callback.';
  }

  return errorDescription || 'Apple Sign-In failed';
}

interface WaitForAppleAuthCompletionOptions {
  attempt: AppleAuthAttempt;
  signInRequest: Promise<unknown>;
  timeoutMs?: number;
}

export function waitForAppleAuthCompletion({
  attempt,
  signInRequest,
  timeoutMs = APPLE_AUTH_RESULT_TIMEOUT_MS,
}: WaitForAppleAuthCompletionOptions): Promise<AppleLoginPayload> {
  return new Promise((resolve, reject) => {
    let settled = false;
    let deferredError: unknown = null;
    let timeoutId = 0;

    const cleanup = () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorage);
      document.removeEventListener(
        'AppleIDSignInOnSuccess',
        handleDomSuccess as EventListener
      );
      document.removeEventListener(
        'AppleIDSignInOnFailure',
        handleDomFailure as EventListener
      );
      window.clearTimeout(timeoutId);
      clearAppleAuthAttempt(attempt.state);
      clearAppleAuthResult(attempt.state);
    };

    const settleWithError = (error: unknown) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      reject(error instanceof Error ? error : new Error(formatAppleAuthError(error)));
    };

    const settleWithSuccess = (payload: AppleLoginPayload) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      resolve(payload);
    };

    const handlePotentialResult = (candidate: unknown): boolean => {
      const payload = normalizeAppleAuthResponse(candidate);
      if (payload) {
        const record = asRecord(candidate);
        const state =
          readString(record?.state) ??
          readString(asRecord(record?.authorization)?.state);

        if (state && state !== attempt.state) {
          return false;
        }

        settleWithSuccess(payload);
        return true;
      }

      const errorCode = getAppleErrorCode(candidate);
      if (errorCode) {
        const record = asRecord(candidate);
        const state = readString(record?.state);
        if (state && state !== attempt.state) {
          return false;
        }

        settleWithError(new Error(formatAppleAuthError(candidate)));
        return true;
      }

      return false;
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      const messageData = asRecord(event.data);
      if (!messageData || messageData.type !== APPLE_AUTH_MESSAGE_TYPE) {
        return;
      }

      handlePotentialResult(messageData.payload);
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== getAppleAuthResultStorageKey(attempt.state) || !event.newValue) {
        return;
      }

      try {
        const payload = JSON.parse(event.newValue) as AppleAuthBridgeResult;
        handlePotentialResult(payload);
      } catch {
        // Ignore malformed storage payloads.
      }
    };

    const handleDomSuccess = (event: Event) => {
      const customEvent = event as CustomEvent<{ data?: unknown }>;
      handlePotentialResult(customEvent.detail?.data ?? customEvent.detail);
    };

    const handleDomFailure = (event: Event) => {
      const customEvent = event as CustomEvent<{ error?: unknown }>;
      handlePotentialResult(customEvent.detail?.error ?? customEvent.detail);
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorage);
    document.addEventListener(
      'AppleIDSignInOnSuccess',
      handleDomSuccess as EventListener
    );
    document.addEventListener(
      'AppleIDSignInOnFailure',
      handleDomFailure as EventListener
    );

    const existingResult = consumeAppleAuthBridgeResult(attempt.state);
    if (existingResult && handlePotentialResult(existingResult)) {
      return;
    }

    signInRequest
      .then((response) => {
        handlePotentialResult(response);
      })
      .catch((error) => {
        const formattedError = formatAppleAuthError(error);
        if (formattedError === 'Apple Sign-In was cancelled') {
          settleWithError(new Error(formattedError));
          return;
        }

        deferredError = error;
      });

    timeoutId = window.setTimeout(() => {
      if (deferredError) {
        settleWithError(new Error(formatAppleAuthError(deferredError)));
        return;
      }

      settleWithError(
        new Error('Apple Sign-In timed out before Insight Atlas received the callback.')
      );
    }, timeoutMs);
  });
}
